import { http, HttpResponse } from 'msw'
import { z } from 'zod'
import { foldEvents } from '../src/components/freeform/run/freeform-run.ts'
import type {
  FreeformUploadSessionPaginatedResponse,
  FreeformUploadSessionResponse,
  FreeformUploadSessionState,
  FreeformUploadSessionSummaryResponse,
} from '../src/utils/api-client/api-schemas.ts'
import { type FreeformUploadEvent, FreeformUploadEventSchema } from '../src/utils/api-client/sse-schemas.ts'
import { buildPath } from '../src/utils/build-path.ts'
import { nowMs as currentMs, msToIso } from '../src/utils/date-utils.ts'
import transcriptJson from './fixtures/freeform/sandbox-run.json' with { type: 'json' }
import { signedIn } from './session-cookie.ts'

// A stateful model of Findings Intake runs shared by dev:mocks, Storybook, vitest and Playwright. An upload
// replays `fixtures/freeform/sandbox-run.json` on a compressed clock: a run of analysis-service's
// association over the benchmark's real repositories (Apache Roller, Juice Shop, WebGoat), recorded as the
// platform relays it, so a run started in the UI reads the report, places each finding and finishes with
// the scans the benchmark expects.

/** How long a demo run takes from upload to its terminal event. */
export const FREEFORM_DEMO_DURATION_MS = 40_000

/** The seeded finished run, so stories and tests can open it by id. */
export const FREEFORM_COMPLETED_SESSION_ID = '0f5e1c0d-f0f0-4000-8000-000000000001'

/** The report the seeded run read. */
export const FREEFORM_DEMO_FILENAME = 'sandbox_appsec_review.md'

const ORGANIZATION_PATH = '/api/v1/organizations/:organizationId/freeform-uploads'

/** The recorded run, every event of it, in the order the platform sent them. */
export const FREEFORM_DEMO_EVENTS: readonly FreeformUploadEvent[] = transcriptJson.map(event =>
  FreeformUploadEventSchema.parse(event)
)

const DemoSessionSchema = z.object({
  id: z.string(),
  createdAtMs: z.number(),
  createdBy: z.string(),
  filenames: z.array(z.string()),
  durationMs: z.number(),
  failure: z.string().optional(),
})
type DemoSession = z.infer<typeof DemoSessionSchema>

const sessions = new Map<string, DemoSession>()

// dev:mocks keeps uploads across reloads, so a run page can be reopened; tests start from the seed alone.
const STORAGE_KEY = 'pixee-mocks/freeform-sessions'

const browserStorage = (): Storage | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage
  } catch {
    return undefined
  }
}

const persistSessions = () =>
  browserStorage()?.setItem(
    STORAGE_KEY,
    JSON.stringify([...sessions.values()].filter(session => session.id !== FREEFORM_COMPLETED_SESSION_ID))
  )

const restoreSessions = () => {
  const stored = browserStorage()?.getItem(STORAGE_KEY)
  // A corrupt or truncated value is dropped rather than thrown, so it can't break dev:mocks or Storybook.
  const parsed = z
    .string()
    .transform((json, context) => {
      try {
        const value: unknown = JSON.parse(json)
        return value
      } catch {
        context.addIssue({ code: 'custom', message: 'stored sessions are not JSON' })
        return z.NEVER
      }
    })
    .pipe(z.array(DemoSessionSchema))
    .safeParse(stored ?? '[]')
  for (const session of parsed.data ?? []) {
    sessions.set(session.id, session)
  }
}

const lastProgressSeq = Math.max(...FREEFORM_DEMO_EVENTS.map(event => (event.type === 'progress' ? event.seq : 0)))

/**
 * When each event is sent, as a share of the run: progress frames spread across it by seq, the opening status
 * at its start, and what the platform sends once ingest returns (files, scans, the outcome) at its end.
 */
const shareOf = (event: FreeformUploadEvent, index: number) => {
  if (event.type === 'progress') {
    return event.seq / (lastProgressSeq + 1)
  }
  return index === 0 ? 0 : 1
}

const DEMO_RUN = foldEvents(FREEFORM_DEMO_EVENTS)

/** A scan's findings as the platform stores them: each at the file it was placed in, with no line. */
const scanFindingsOf = (scanId: string, repoFullName: string) =>
  DEMO_RUN.findings.flatMap(finding => {
    const location = finding.locations.find(candidate => candidate.state === 'verified')
    return finding.repository === repoFullName && location
      ? [
          {
            finding_id: `${scanId}-finding-${finding.number}`,
            title: finding.title,
            severity: finding.severity,
            path: location.path,
          },
        ]
      : []
  })

/** The events a session has sent by `nowMs`. */
export const freeformEventsAt = (session: DemoSession, nowMs: number): FreeformUploadEvent[] => {
  const elapsed = nowMs - session.createdAtMs
  // Each frame is stamped when this session sent it, as the platform's would be, not when it was recorded.
  const sent = FREEFORM_DEMO_EVENTS.flatMap((event, index) => {
    const offsetMs = shareOf(event, index) * session.durationMs
    if (offsetMs > elapsed) {
      return []
    }
    return [event.type === 'progress' ? { ...event, ts: msToIso(session.createdAtMs + offsetMs) } : event]
  })
  if (session.failure && elapsed >= session.durationMs) {
    return [
      ...sent.filter(event => event.type === 'status' || event.type === 'progress'),
      { type: 'status', phase: 'failed', detail: session.failure },
      { type: 'error', reason: session.failure },
    ]
  }
  return sent
}

const stateAt = (session: DemoSession, nowMs: number): FreeformUploadSessionState =>
  nowMs - session.createdAtMs < session.durationMs ? 'parsing' : session.failure ? 'failed' : 'completed'

const DEMO_ORGANIZATION_ID = 'default-organization'

const linksOf = (sessionId: string) => {
  const self = buildPath(`${ORGANIZATION_PATH}/:sessionId`, { organizationId: DEMO_ORGANIZATION_ID, sessionId })
  return { self: { href: self }, events: { href: `${self}/events` } }
}

const scanLinksOf = (scanId: string) => {
  const scan = { href: buildPath('/api/v1/scans/:scanId', { scanId }) }
  return { self: scan, scan }
}

export const freeformSessionSnapshot = (session: DemoSession, nowMs = currentMs()): FreeformUploadSessionResponse => {
  const status = stateAt(session, nowMs)
  const isFinished = status === 'completed'
  const events = FREEFORM_DEMO_EVENTS
  return {
    _links: linksOf(session.id),
    id: session.id,
    status,
    failure_reason: status === 'failed' ? session.failure : undefined,
    created_by: session.createdBy,
    created_at: msToIso(session.createdAtMs),
    updated_at: msToIso(Math.min(nowMs, session.createdAtMs + session.durationMs)),
    files: session.filenames.map(filename => ({
      filename,
      parse_status: status === 'parsing' ? ('pending' as const) : ('parsed' as const),
    })),
    scans: isFinished
      ? events.flatMap(event =>
          event.type === 'repo_scan'
            ? [
                {
                  _links: scanLinksOf(event.scanId),
                  scan_id: event.scanId,
                  repo_full_name: event.repoFullName,
                  finding_count: event.findingCount,
                  findings: scanFindingsOf(event.scanId, event.repoFullName),
                },
              ]
            : []
        )
      : [],
    unassociated: isFinished
      ? events.flatMap(event => (event.type === 'unassociated' ? (event.findings ?? []) : []))
      : [],
  }
}

const summaryOf = (session: DemoSession, nowMs: number): FreeformUploadSessionSummaryResponse => {
  const snapshot = freeformSessionSnapshot(session, nowMs)
  return {
    _links: snapshot._links,
    id: snapshot.id,
    status: snapshot.status,
    failure_reason: snapshot.failure_reason,
    created_by: snapshot.created_by,
    created_at: snapshot.created_at,
    updated_at: snapshot.updated_at,
    filenames: session.filenames,
    scan_count: snapshot.scans.length,
    finding_count: snapshot.scans.reduce((sum, scan) => sum + scan.finding_count, 0),
    unassociated_count: snapshot.unassociated.length,
  }
}

/** Opens a demo session, started `startedAgoMs` ago. */
export const addFreeformSession = ({
  filenames = [FREEFORM_DEMO_FILENAME],
  createdBy = 'pixee-admin',
  startedAgoMs = 0,
  durationMs = FREEFORM_DEMO_DURATION_MS,
  failure,
  id = crypto.randomUUID(),
}: Partial<Omit<DemoSession, 'createdAtMs'>> & { startedAgoMs?: number } = {}): DemoSession => {
  const session: DemoSession = {
    id,
    createdAtMs: currentMs() - startedAgoMs,
    createdBy,
    filenames,
    durationMs,
    failure,
  }
  sessions.set(id, session)
  persistSessions()
  return session
}

const seedFinishedSession = () =>
  addFreeformSession({ id: FREEFORM_COMPLETED_SESSION_ID, startedAgoMs: 60 * 60 * 1000 })

export const resetFreeformState = () => {
  sessions.clear()
  browserStorage()?.removeItem(STORAGE_KEY)
  seedFinishedSession()
}

// Restored before seeding: seeding persists, which would otherwise overwrite the stored uploads.
restoreSessions()
seedFinishedSession()

const notFound = () => new HttpResponse(null, { status: 404 })

/** One event as the platform frames it: its position in the session's events, from 1, is its SSE id. */
const sseFrame = (event: FreeformUploadEvent, index: number) =>
  `id: ${index + 1}\nevent: ${event.type}\ndata: ${JSON.stringify(event)}\n\n`

const framesFrom = (events: readonly FreeformUploadEvent[], first: number) =>
  events
    .slice(first)
    .map((event, offset) => sseFrame(event, first + offset))
    .join('')

/** How many events a connection's `Last-Event-ID` says it already has. */
const resumedAfter = (request: Request) => Number(request.headers.get('Last-Event-ID') ?? 0) || 0

const SSE_HEADERS = { 'Content-Type': 'text/event-stream', 'Cache-Control': 'no-cache' }

const isTerminal = (event: FreeformUploadEvent) => event.type === 'done' || event.type === 'error'

export type FreeformHandlersOptions = {
  /**
   * Whether the events endpoint keeps the connection open and tails new events. Playwright's route
   * interception only fulfils complete bodies, so e2e runs answer with the events so far and close,
   * and the client reconnects.
   */
  isStreaming?: boolean
  /** How often a streaming connection checks for new events. */
  tickMs?: number
  /** How long an upload made through the POST takes to finish. */
  durationMs?: number
}

export const freeformHandlers = ({
  isStreaming = true,
  tickMs = 250,
  durationMs = FREEFORM_DEMO_DURATION_MS,
}: FreeformHandlersOptions = {}) => ({
  postFreeformUpload: http.post(
    ORGANIZATION_PATH,
    signedIn(async ({ request }) => {
      const form = await request.formData()
      const filenames = form.getAll('files').flatMap(part => (typeof part === 'string' ? [] : [part.name]))
      const session = addFreeformSession({ filenames, durationMs })
      return HttpResponse.json({ id: session.id, status: 'parsing', file_count: filenames.length }, { status: 202 })
    })
  ),

  getFreeformUploadSessions: http.get(
    ORGANIZATION_PATH,
    signedIn(({ request }) => {
      const url = new URL(request.url)
      const number = Number(url.searchParams.get('page-number') ?? 0)
      const size = Number(url.searchParams.get('page-size') ?? 10)
      const nowMs = currentMs()
      const all = [...sessions.values()].sort((a, b) => b.createdAtMs - a.createdAtMs)
      const items = all.slice(number * size, number * size + size).map(session => summaryOf(session, nowMs))
      return HttpResponse.json({
        _embedded: { items },
        _links: {
          self: { href: `${url.pathname}${url.search}` },
          first: { href: `${url.pathname}?page-number=0&page-size=${size}` },
        },
        page: { number, size },
        total: all.length,
      } satisfies FreeformUploadSessionPaginatedResponse)
    })
  ),

  getFreeformUploadSession: http.get(
    `${ORGANIZATION_PATH}/:sessionId`,
    signedIn(({ params }) => {
      const session = sessions.get(String(params.sessionId))
      return session ? HttpResponse.json(freeformSessionSnapshot(session)) : notFound()
    })
  ),

  getFreeformUploadEvents: http.get(
    `${ORGANIZATION_PATH}/:sessionId/events`,
    signedIn(({ params, request }) => {
      const session = sessions.get(String(params.sessionId))
      if (!session) {
        return notFound()
      }
      // Like the platform, a connection gets every event after its Last-Event-ID (all of them without one),
      // and the stream ends with the run.
      const after = resumedAfter(request)
      let sent = freeformEventsAt(session, currentMs())
      if (!isStreaming || sent.some(isTerminal)) {
        return new HttpResponse(framesFrom(sent, after), { headers: SSE_HEADERS })
      }
      const encoder = new TextEncoder()
      let timer: ReturnType<typeof setInterval> | undefined
      const stream = new ReadableStream<Uint8Array>({
        start: controller => {
          controller.enqueue(encoder.encode(framesFrom(sent, after)))
          const stop = () => {
            clearInterval(timer)
            try {
              controller.close()
            } catch {
              // Already closed by the client going away.
            }
          }
          request.signal.addEventListener('abort', stop)
          timer = setInterval(() => {
            const now = freeformEventsAt(session, currentMs())
            const fresh = now.slice(sent.length)
            if (fresh.length > 0) {
              controller.enqueue(encoder.encode(framesFrom(now, Math.max(sent.length, after))))
            }
            sent = now
            if (fresh.some(isTerminal)) {
              stop()
            }
          }, tickMs)
        },
        cancel: () => clearInterval(timer),
      })
      return new HttpResponse(stream, { headers: SSE_HEADERS })
    })
  ),
})

/** When the stories' sessions started; stories pin their clock so their snapshots hold still. */
export const FREEFORM_STORY_STARTED_AT_MS = Date.parse('2026-10-21T15:00:00Z')

/** A demo session that started at the stories' fixed time, left out of the model so it never moves. */
export const fixedFreeformSession = (overrides: Partial<DemoSession> = {}): DemoSession => ({
  id: 'story-session',
  createdAtMs: FREEFORM_STORY_STARTED_AT_MS,
  createdBy: 'pixee-admin',
  filenames: [FREEFORM_DEMO_FILENAME],
  durationMs: FREEFORM_DEMO_DURATION_MS,
  ...overrides,
})

/**
 * Handlers that answer for `pinned` as they stood at `nowMs`. Each stream sends what its session had sent by
 * then and closes, so a story never holds a connection open.
 */
export const freeformSnapshotHandlers = (pinned: readonly DemoSession[], nowMs: number) => ({
  getFreeformUploadSessions: http.get(ORGANIZATION_PATH, () =>
    HttpResponse.json({
      _embedded: { items: pinned.map(session => summaryOf(session, nowMs)) },
      _links: { self: { href: ORGANIZATION_PATH }, first: { href: ORGANIZATION_PATH } },
      page: { number: 0, size: 10 },
      total: pinned.length,
    } satisfies FreeformUploadSessionPaginatedResponse)
  ),
  getFreeformUploadSession: http.get(`${ORGANIZATION_PATH}/:sessionId`, ({ params }) => {
    const session = pinned.find(candidate => candidate.id === params.sessionId)
    return session ? HttpResponse.json(freeformSessionSnapshot(session, nowMs)) : notFound()
  }),
  getFreeformUploadEvents: http.get(`${ORGANIZATION_PATH}/:sessionId/events`, ({ params }) => {
    const session = pinned.find(candidate => candidate.id === params.sessionId)
    return session
      ? new HttpResponse(framesFrom(freeformEventsAt(session, nowMs), 0), { headers: SSE_HEADERS })
      : notFound()
  }),
})
